/** Les 4xx qui demandent d'attendre et de recommencer, pas de renoncer. */
const RETRYABLE_CLIENT_STATUSES = new Set([408, 425, 429])

/**
 * Faut-il effacer le jeton, ou seulement constater que le serveur n'a pas répondu ?
 *
 * L'initialisation effaçait le jeton sur TOUTE erreur du premier appel. Résultat : redémarrer la
 * gateway déconnectait la session, alors que le jeton était parfaitement valide — il est signé
 * RS256 avec une clé de fichier et ne porte aucune expiration. On perdait une session vivante pour
 * une indisponibilité de deux secondes.
 *
 * La frontière est celle du protocole. Une 4xx dit que la requête est en tort : le jeton ne
 * guérira pas d'un nouvel essai, on l'oublie. Une 5xx, une panne réseau, une gateway qui
 * redémarre : c'est en face que ça cloche, la session est gardée et l'appel se retentera.
 *
 * Trois 4xx font exception, parce qu'elles demandent elles-mêmes qu'on réessaie : 408 (le serveur
 * a cessé d'attendre), 425 (trop tôt), 429 (trop souvent).
 *
 * Se limiter à 401/403, comme au premier jet, laissait un trou : un jeton mort répondant 400 ou
 * 422 n'était JAMAIS effacé. Il restait en localStorage, et chaque chargement repayait les essais
 * avant d'afficher la connexion — sans fin, sans jamais se nettoyer.
 */
export function shouldForgetSession(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const candidate = error as {
    status?: number
    name?: string
    response?: { status?: number }
  }
  const status = candidate.status ?? candidate.response?.status
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return !RETRYABLE_CLIENT_STATUSES.has(status)
  }

  // Un jeton que le client n'arrive pas à décoder ne guérira pas d'un nouvel essai.
  if (candidate.name === 'InvalidTokenError') return true

  return false
}
